import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { cn } from "../lib/utils";
import { useAuth } from "../hooks/useAuth";
import { useClickOutside } from "../../hooks/useClickOutside";

/** Extract up to 2 initials from a full name. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserProfileBadge() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useClickOutside(ref, () => setOpen(false), open);

  if (!user) return null;

  const initials = getInitials(user.name);

  function handleLogout() {
    setOpen(false);
    logout();
    navigate("/copilot/login", { replace: true });
  }

  return (
    <div ref={ref} className="relative">
      {/* Initials badge button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={`Profile: ${user.name}`}
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full",
          "bg-brand text-[0.75rem] font-semibold text-text-inverse",
          "transition-opacity active:opacity-80"
        )}
      >
        {initials}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            "absolute left-0 top-full z-50 mt-1 w-56 rounded-xl",
            "border border-border bg-surface-elevated shadow-lg"
          )}
        >
          {/* User info */}
          <div className="border-b border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                  "bg-brand text-sm font-semibold text-text-inverse"
                )}
              >
                {initials}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text-primary">
                  {user.name}
                </p>
                <p className="truncate text-xs text-text-secondary">
                  {user.email}
                </p>
                <span
                  className={cn(
                    "mt-1 inline-block rounded-full px-2 py-0.5",
                    "text-[0.625rem] font-medium uppercase tracking-wide",
                    user.role === "admin"
                      ? "bg-accent-500/15 text-accent-600"
                      : "bg-brand/10 text-brand"
                  )}
                >
                  {user.role}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="p-1">
            <button
              type="button"
              onClick={handleLogout}
              className={cn(
                "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm",
                "text-error transition-colors active:bg-surface-inset"
              )}
            >
              <LogOut className="h-4 w-4" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
